import { and, desc, eq, isNull, or, sql } from 'drizzle-orm'

import type { Database, Queryable } from '../client.ts'
import { err, ok, type Result } from '../result.ts'
import {
  type DiscountCode,
  type DiscountRedemption,
  discountCode,
  discountRedemption,
  type NewDiscountCode,
} from '../schema/discounts.ts'
import { type DbError, dbConflict, dbError, isUniqueViolation } from './errors.ts'

/**
 * Reading, minting and SPENDING a discount code (spec §14 · H1).
 *
 * The whole file exists for one statement — {@link claimDiscountCode}. Every
 * other question about a code is a read that can be wrong by a moment and
 * recover; "is there a use left" cannot, because two checkouts asking it at the
 * same instant both get yes and both go through. That one is an atomic
 * conditional update, and it is the only place the limit is real.
 */

/** Look a code up as somebody typed it — case-insensitively. */
export async function findDiscountCode(
  db: Queryable,
  code: string,
): Promise<Result<DiscountCode | null, DbError>> {
  try {
    const [row] = await db
      .select()
      .from(discountCode)
      .where(sql`lower(${discountCode.code}) = lower(${code.trim()})`)
      .limit(1)
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to look up a discount code', cause))
  }
}

/**
 * Spend one use, or refuse.
 *
 * `null` back means the code ran out between the customer typing it and the
 * order committing — which is a real race on a single-use code and the reason
 * this is a conditional UPDATE rather than a read followed by an increment.
 * The predicate carries the WHOLE rule that can change under a race: still
 * active, still in season, still with a use left.
 *
 * Called INSIDE the order's transaction, so a rolled-back checkout hands the
 * use back by rolling back with it.
 */
export async function claimDiscountCode(
  db: Queryable,
  input: { readonly id: string; readonly now: Date },
): Promise<Result<DiscountCode | null, DbError>> {
  try {
    const [row] = await db
      .update(discountCode)
      .set({ usedCount: sql`${discountCode.usedCount} + 1`, updatedAt: input.now })
      .where(
        and(
          eq(discountCode.id, input.id),
          eq(discountCode.isActive, true),
          or(
            isNull(discountCode.maxUses),
            sql`${discountCode.usedCount} < ${discountCode.maxUses}`,
          ),
          or(isNull(discountCode.validFrom), sql`${discountCode.validFrom} <= ${input.now}`),
          or(isNull(discountCode.validUntil), sql`${discountCode.validUntil} > ${input.now}`),
        ),
      )
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to claim a discount code', cause))
  }
}

/** Record which order spent it, and what it took off. Frozen, both. */
export async function insertRedemption(
  db: Queryable,
  values: {
    readonly discountCodeId: string
    readonly orderId: string
    readonly userId: string | null
    readonly code: string
    readonly amountNetMinor: number
  },
): Promise<Result<DiscountRedemption, DbError>> {
  try {
    const [row] = await db.insert(discountRedemption).values(values).returning()
    if (!row) return err(dbError('Redemption insert returned no row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to record a code redemption', cause))
  }
}

/** Every code, newest first — the Admin's list. */
export async function listDiscountCodes(
  db: Database,
  limit = 200,
): Promise<Result<DiscountCode[], DbError>> {
  try {
    return ok(
      await db.select().from(discountCode).orderBy(desc(discountCode.createdAt)).limit(limit),
    )
  } catch (cause) {
    return err(dbError('Failed to list discount codes', cause))
  }
}

/** Mint one. The unique index on `lower(code)` is what refuses a duplicate. */
/**
 * Has this customer already been given a consultation bonus (spec §12 · I8)?
 *
 * One consultation, one code. Keyed on the REDEMPTION's owner rather than on
 * the code's creator, because the code is minted for them by the system and
 * nobody at FLOU is its author.
 */
export async function hasConsultationBonus(
  db: Database,
  userId: string | null,
): Promise<Result<boolean, DbError>> {
  if (!userId) return ok(false)
  try {
    const rows = await db
      .select({ id: discountCode.id })
      .from(discountCode)
      .where(and(eq(discountCode.isConsultationBonus, true), eq(discountCode.ownerUserId, userId)))
      .limit(1)
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to check for a consultation bonus', cause))
  }
}

export async function insertDiscountCode(
  db: Database,
  values: NewDiscountCode,
): Promise<Result<DiscountCode, DbError>> {
  try {
    const [row] = await db.insert(discountCode).values(values).returning()
    if (!row) return err(dbError('Discount code insert returned no row'))
    return ok(row)
  } catch (cause) {
    if (isUniqueViolation(cause)) return err(dbConflict('Taki kod już istnieje.'))
    return err(dbError('Failed to create a discount code', cause))
  }
}

/**
 * Turn one off.
 *
 * There is no delete, and that is deliberate: `discount_redemption` references
 * the code with `restrict`, because a code somebody actually used is part of an
 * order's money trail. Deactivating is what "removing" means here.
 */
export async function deactivateDiscountCode(
  db: Database,
  id: string,
  now: Date,
): Promise<Result<DiscountCode | null, DbError>> {
  try {
    const [row] = await db
      .update(discountCode)
      .set({ isActive: false, updatedAt: now })
      .where(eq(discountCode.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to deactivate a discount code', cause))
  }
}
